import "./MembersTable.scss";

import { useCallback, useEffect, useState } from "react";
import { Badge } from "react-bootstrap";
import type { CellProps, Column } from "react-table";

import { ActionCell } from "../../../../components/ActionCell/ActionCell";
import { DateColumn } from "../../../../components/CustomizedTable/Columns";
import { PaginatedTable } from "../../../../components/CustomizedTable/PaginatedTable";
import { CheckIcon, RefreshIcon, StopIcon } from "../../../../components/icons";
import { TableCellSm, TableLineSm } from "../../../../components/TableLineSm";
import { TableSearch } from "../../../../components/TableSearch";
import { useMediaQueryContext } from "../../../../contexts/mediaQueryProvider/MediaQueryProvider";
import { useOrganizationContext } from "../../../../contexts/organizations/organizationsContext";
import { useDeleteUser } from "../../../../hooks/reactQueryHooks/useOrganization";
import type { IData } from "../../../../types";
import { EditMemberModal } from "../modals/EditMemberModal/EditMemberModal";
import { RemoveMemberModal } from "../modals/RemoveMemberModal/RemoveMemberModal";

interface IProps {
  data: any;
  pageSize?: number;
  isLoading: boolean;
  totalCount: number;
  pageIndex?: number;
  orgOwnerId: string;
  updateMember: (member: IEditUser) => void;
}

export interface IEditUser {
  firstName: string;
  lastName: string;
  email: string;
  id: string;
}

const MembersTable: React.FC<IProps> = ({
  data,
  pageSize,
  isLoading,
  totalCount,
  pageIndex,
  orgOwnerId,
  updateMember,
}) => {
  const [currentMember, setCurrentMember] = useState<IEditUser | null>(null);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [showDeleteMemberModal, setShowDeleteMemberModal] = useState(false);
  const { currentOrganization } = useOrganizationContext();
  const [showToast, setShowToast] = useState<boolean>(false);
  const [convertedData, setConvertedData] = useState<any>([]);
  const { isLg } = useMediaQueryContext();

  const deleteMutation = useDeleteUser();

  const openEditModal = useCallback(
    (memberEmail: IEditUser) => {
      setCurrentMember(memberEmail);
      setShowEditMemberModal(true);
    },
    [setCurrentMember, setShowEditMemberModal],
  );
  const openDeleteModal = useCallback(
    (memberEmail: IEditUser) => {
      setCurrentMember(memberEmail);
      setShowDeleteMemberModal(true);
    },
    [setCurrentMember],
  );

  const handleClose = () => {
    setShowEditMemberModal(false);
    setShowDeleteMemberModal(false);
    setTimeout(() => {
      setCurrentMember(null);
    }, 300);
  };

  const renderRoles = (props: CellProps<IData>) => {
    const roles = props.row.original.roles;
    const joinedRoles = roles.map((role: any) => role.name).join(", ");

    return <span>{joinedRoles}</span>;
  };

  const renderActions = useCallback(
    (props: CellProps<IData>) => {
      const onlyEdit =
        props.row.original.owner || orgOwnerId === props.row.original.id;

      return (
        <ActionCell
          onDeleteClick={
            onlyEdit
              ? null
              : () =>
                  openDeleteModal({
                    firstName: props.row.original.firstName,
                    lastName: props.row.original.lastName,
                    email: props.row.original.email,
                    id: props.row.original.id,
                  })
          }
          onEditClick={() =>
            openEditModal({
              firstName: props.row.original.firstName,
              lastName: props.row.original.lastName,
              email: props.row.original.email,
              id: props.row.original.id,
            })
          }
        />
      );
    },
    [openDeleteModal, openEditModal, orgOwnerId],
  );

  const renderDate = useCallback((props: CellProps<IData>) => {
    return <DateColumn date={props.row.original.joinedAt} type="long" />;
  }, []);

  const renderName = useCallback(
    ({ row }: CellProps<IData>) => {
      const isOrgOwner = orgOwnerId === row.original.id;
      return (
        <div className="d-flex flex-column members-name-column">
          <span>
            {row?.original?.name}
            {isOrgOwner && (
              <Badge className="chart__label__badge purple mx-2 owner-badge">
                owner
              </Badge>
            )}
          </span>
          <span>{row?.original?.email}</span>
        </div>
      );
    },
    [orgOwnerId],
  );

  const renderStatus = ({ row }: CellProps<IData>) => {
    const state = row?.original?.state;
    return (
      <span className={`members-state members-state-${state}`}>
        {(() => {
          switch (state) {
            case "active":
              return <CheckIcon />;
            case "inactive":
              return <StopIcon />;
            default:
              return <RefreshIcon />;
          }
        })()}
        {state === "tempPassword" ? "temporary password" : state}
      </span>
    );
  };

  const renderSM = useCallback(
    (props: CellProps<IData>) => {
      return (
        <TableLineSm title={renderName(props)} controls={renderActions(props)}>
          <TableCellSm title="Role">{renderRoles(props)}</TableCellSm>
          <TableCellSm title="Joined date">{renderDate(props)}</TableCellSm>
          <TableCellSm title="Status">{renderStatus(props)}</TableCellSm>
        </TableLineSm>
      );
    },
    [renderActions, renderDate, renderName],
  );
  const columns: Column<IData>[] = [
    {
      Header: "Name",
      accessor: "name",
      minWidth: 140,
      width: 200,
      Cell: renderName,
    },
    {
      Header: "Joined date",
      accessor: "joinedAt",
      minWidth: 140,
      width: 200,
      Cell: renderDate,
    },
    {
      Header: "Role",
      minWidth: 140,
      width: 200,
      Cell: renderRoles,
    },
    {
      Header: "Status",
      minWidth: 140,
      width: 200,
      Cell: renderStatus,
    },
    {
      Header: "",
      id: "edit",
      minWidth: 50,
      width: 50,
      maxWidth: 50,
      Cell: renderActions,
    },
  ];

  const columnsSm: Column<IData>[] = [
    {
      Header: "Members",
      accessor: "name",
      Cell: renderSM,
    },
  ];

  const onUserDelete = (userId: string) => {
    deleteMutation.mutate({
      userId,
      organizationId: currentOrganization?.id || "",
    });
  };

  useEffect(() => {
    if (deleteMutation.isSuccess) {
      setShowDeleteMemberModal(false);
      setShowToast(true);
    }
  }, [deleteMutation.isSuccess]);

  useEffect(() => {
    if (showToast) {
      setTimeout(() => {
        setShowToast(false);
      });
    }
  }, [showToast]);

  useEffect(() => {
    if (data) {
      const convertedData = data?.map((item: any) => {
        return {
          ...item,
          name: `${item.firstName || ""} ${item.lastName || ""}`,
          role: item.roles[0] && item.roles[0].name,
        };
      });
      setConvertedData(convertedData);
    }
  }, [data]);

  return (
    <>
      {showEditMemberModal && (
        <EditMemberModal
          member={currentMember}
          closeModal={handleClose}
          updateMember={updateMember}
        />
      )}
      {showDeleteMemberModal && (
        <RemoveMemberModal
          member={currentMember}
          closeModal={handleClose}
          onUserDelete={onUserDelete}
          isProcessing={deleteMutation.isLoading}
        />
      )}
      <div className="custom-header-wrapper">
        <div className="align-items-center position-relative">
          <TableSearch
            label={
              <>
                <strong>Search</strong> through all members
              </>
            }
            placeholder="Type to filter..."
          />
        </div>
      </div>
      <PaginatedTable
        columns={isLg ? columnsSm : columns}
        data={convertedData || []}
        itemsPerPage={pageSize}
        totalItems={totalCount}
        isLoading={isLoading}
        pageIndex={pageIndex}
      />
    </>
  );
};

export { MembersTable };
